import { useSyncExternalStore } from 'react';

/**
 * Whether the search palette is open.
 *
 * ── A STORE RATHER THAN A `useState` IN THE SHELL ─────────────────────────────────────────────
 *
 * Four things open this box and they are not near each other in the tree: the rail's Search item,
 * the phone's More sheet, the global Ctrl+K listener, and the empty state of a list page that has
 * nothing to show. Threading a setter to all four means `__root.tsx` holds state on behalf of
 * components two files away, and every one of them grows a prop that exists only to be passed on.
 *
 * Same tiny external-store shape as `lib/railCollapse.ts` and `lib/theme.ts`, for the same reason
 * those are not context: it is read in a handful of places and changes rarely, so a provider would
 * re-render the whole shell to move a boolean.
 *
 * NOT PERSISTED, unlike those two. A palette that reopened itself on the next page load would be a
 * console that greets you with a modal — this is the state of a moment, not a preference.
 */

let open = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setSearchOpen(next: boolean): void {
  if (open === next) return;
  open = next;
  emit();
}

export function openSearch(): void {
  setSearchOpen(true);
}

/** Whether the palette is open. */
export function useSearchOpen(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => open,
    () => false, // server/prerender snapshot
  );
}
